/**
 * WizardStepSchedule — Step 4: Planning, congés, jours fériés.
 * Extracted from ContractWizard.jsx for maintainability.
 * This was the largest step (~320 lines of JSX).
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, CalendarDays, Briefcase, MapPin, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DAYS_FR } from '../../utils/constants';
import { useState, useRef, useEffect, useCallback } from 'react';

/** Mini autocomplete for city/postal code using BAN API */
const CityAutocomplete = ({ value, onSelect }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value && !query) setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const type = /^\d/.test(q) ? 'municipality' : 'municipality';
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&type=${type}&limit=5&autocomplete=1`);
      const data = await res.json();
      if (data?.features) {
        const unique = new Map();
        data.features.forEach(f => {
          const key = f.properties.postcode;
          if (!unique.has(key)) {
            unique.set(key, {
              label: `${f.properties.postcode} — ${f.properties.city}`,
              postal_code: f.properties.postcode,
              city: f.properties.city,
              context: f.properties.context,
            });
          }
        });
        setSuggestions(Array.from(unique.values()));
        setOpen(true);
      }
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    // Also allow raw postal code entry
    const digits = val.replace(/\D/g, '').slice(0, 5);
    if (digits.length === 5) onSelect(digits);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handlePick = (s) => {
    setQuery(`${s.postal_code} — ${s.city}`);
    setOpen(false);
    setSuggestions([]);
    onSelect(s.postal_code);
  };

  return (
    <div ref={containerRef} className="relative w-48">
      <div className="relative">
        <Input
          value={query}
          onChange={handleInput}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder="Ville ou code postal"
          className="h-8 text-sm pr-8"
          data-testid="wizard-postal-code"
        />
        {loading && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-[var(--text-muted)]" />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-64 rounded-lg border shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--neutral-200)' }}>
          {suggestions.map((s) => (
            <button key={`${s.postal_code}-${s.city}`} type="button" onClick={() => handlePick(s)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--primary-50)] transition-colors"
              data-testid={`wizard-city-suggestion-${s.postal_code}`}
            >
              <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{s.postal_code}</span>
              <span className="ml-1.5" style={{ color: 'var(--text-secondary)' }}>— {s.city}</span>
              <span className="ml-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>({s.context})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const WizardStepSchedule = ({
  data,
  onChange,
  availableHolidays,
  loadingHolidays,
  onDayToggle,
  onScheduleChange,
  onPostalCodeChange,
  onToggleHolidayWorked,
  onToggleAllHolidays,
  weeklyHours,
  children,
}) => (
  <div className="space-y-5">
    {/* ── Working days & schedule ── */}
    <div className="space-y-3">
      <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
        <Clock className="h-4 w-4" style={{ color: 'var(--primary-400)' }} /> Jours et horaires de travail
      </h3>
      <div className="space-y-2">
        {DAYS_FR.map((day) => {
          const isSelected = data.working_days.includes(day.id);
          const schedule = data.daily_schedule[day.id] || { start: '08:00', end: '18:00' };
          return (
            <div key={day.id} className="flex items-center gap-3 p-2 border border-[var(--neutral-200)] rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox id={day.id} checked={isSelected} onCheckedChange={(checked) => onDayToggle(day.id, checked)} data-testid={`day-${day.id}`} />
                <Label htmlFor={day.id} className="font-medium cursor-pointer w-20 text-sm">{day.label}</Label>
              </div>
              {isSelected && (
                <div className="flex items-center gap-2 flex-1">
                  <Input type="time" value={schedule.start} onChange={(e) => onScheduleChange(day.id, 'start', e.target.value)} className="w-24 h-8 text-sm" data-testid={`start-${day.id}`} />
                  <span className="text-[var(--text-secondary)] text-sm">→</span>
                  <Input type="time" value={schedule.end} onChange={(e) => onScheduleChange(day.id, 'end', e.target.value)} className="w-24 h-8 text-sm" data-testid={`end-${day.id}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {data.working_days.length > 0 && (
        <div className="flex items-center gap-2 px-2">
          <Badge variant="outline" className="text-xs border-[var(--primary-400)] text-[var(--primary-400)]">{weeklyHours}h / semaine</Badge>
          <Badge variant="outline" className="text-xs border-[var(--neutral-300)] text-[var(--text-secondary)]">{data.working_days.length} jours</Badge>
        </div>
      )}
    </div>

    <Separator />

    {/* ── Leave configuration ── */}
    <div className="space-y-3">
      <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
        <CalendarDays className="h-4 w-4" style={{ color: 'var(--primary-400)' }} /> Congés & Conditions
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Jour de repos hebdomadaire</Label>
          <Select value={data.weekly_rest_day} onValueChange={(val) => onChange({ ...data, weekly_rest_day: val })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {DAYS_FR.map(d => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Délai de prévenance (jours)</Label>
          <Input type="number" min="1" max="90" value={data.planning_notice_days} onChange={(e) => onChange({ ...data, planning_notice_days: e.target.value })} data-testid="wizard-notice-days" />
        </div>
        <div>
          <Label>Mode d'acquisition des congés</Label>
          <Select value={data.leave_mode} onValueChange={(val) => onChange({ ...data, leave_mode: val })}>
            <SelectTrigger data-testid="wizard-leave-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ouvrables">Jours ouvrables (6j/sem)</SelectItem>
              <SelectItem value="ouvres">Jours ouvrés (5j/sem)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Mode de paiement des congés</Label>
          <Select value={data.leave_payment_mode} onValueChange={(val) => onChange({ ...data, leave_payment_mode: val })}>
            <SelectTrigger data-testid="wizard-leave-payment"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="maintien">Maintien de salaire</SelectItem>
              <SelectItem value="dixieme">1/10ème de la rémunération</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      {/* ── Year type & leave policy ── */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Briefcase className="h-4 w-4" style={{ color: 'var(--secondary-400)' }} /> Type de contrat & Congés
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Type d'année</Label>
            <Select value={data.contract_year_type || 'complete'} onValueChange={(val) => onChange({ ...data, contract_year_type: val })}>
              <SelectTrigger data-testid="wizard-year-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="complete">Année complète (52 sem.)</SelectItem>
                <SelectItem value="incomplete">Année incomplète (≤46 sem.)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
              {(data.contract_year_type || 'complete') === 'complete'
                ? '47 sem. travaillées + 5 sem. congés inclus dans la mensualisation'
                : 'Congés payés séparément (10% ou maintien de salaire)'}
            </p>
          </div>
          {(data.contract_year_type || 'complete') === 'complete' && (
            <div>
              <Label>Salaire mensuel (mensualisation) €</Label>
              <Input type="number" step="0.01" min="0" placeholder="Ex: 850.00" value={data.monthly_salary || ''} onChange={(e) => onChange({ ...data, monthly_salary: e.target.value })} data-testid="wizard-monthly-salary" />
            </div>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--neutral-200)] bg-[var(--neutral-50)]">
            <div>
              <Label className="font-semibold text-sm cursor-pointer">Acceptation automatique des congés</Label>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">Les demandes de congés sont approuvées sans validation du parent</p>
            </div>
            <Switch checked={data.auto_approve_leave || false} onCheckedChange={(checked) => onChange({ ...data, auto_approve_leave: checked })} data-testid="wizard-auto-approve-leave" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--neutral-200)] bg-[var(--neutral-50)]">
            <div>
              <Label className="font-semibold text-sm cursor-pointer">Autoriser les congés anticipés</Label>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">La nounou peut prendre plus de congés que le solde acquis (solde négatif possible)</p>
            </div>
            <Switch checked={data.allow_anticipation_leave || false} onCheckedChange={(checked) => onChange({ ...data, allow_anticipation_leave: checked })} data-testid="wizard-allow-anticipation" />
          </div>
        </div>
      </div>

      {/* ── Postal code / Department ── */}
      <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--neutral-200)] bg-[var(--neutral-50)]">
        <div className="flex items-center gap-3">
          <MapPin className="h-5 w-5" style={{ color: 'var(--primary-400)' }} />
          <div>
            <Label className="font-semibold text-sm">Lieu d'accueil (jours fériés régionaux)</Label>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">Saisissez la ville ou le code postal — le département sera détecté automatiquement</p>
          </div>
        </div>
        <CityAutocomplete
          value={data.postal_code}
          onSelect={(postalCode) => onPostalCodeChange(postalCode)}
        />
      </div>
      {data.department && (
        <Badge variant="outline" className="text-xs border-[var(--primary-400)] text-[var(--primary-400)]">
          Département : {data.department}
          {['57','67','68'].includes(data.department) && ' (Alsace-Moselle — +2 jours fériés)'}
          {['971','972','973','974','976'].includes(data.department) && ' (DROM — +1 jour férié local)'}
        </Badge>
      )}
    </div>

    <Separator />

    {/* ── Holidays ── */}
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <CalendarDays className="h-4 w-4 text-[#E17A73]" /> Jours fériés
        </h3>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => onToggleAllHolidays(true)} data-testid="wizard-holidays-all-worked">Tous travaillés</Button>
          <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => onToggleAllHolidays(false)} data-testid="wizard-holidays-all-rest">Tous chômés</Button>
        </div>
      </div>

      {/* Legal info banner */}
      <div className="p-3 rounded-lg border border-[var(--primary-400)]/20 bg-[var(--primary-400)]/5 space-y-1.5">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" style={{ color: 'var(--primary-400)' }} />
          <div className="text-xs space-y-1" style={{ color: 'var(--text-secondary)' }}>
            <p><strong>Cadre légal :</strong> Un jour férié est rémunéré uniquement s'il correspond à un <strong>jour habituellement travaillé</strong> et si la nounou a travaillé le <strong>jour ouvré précédent et suivant</strong> (sauf absence justifiée).</p>
            {(data.contract_year_type || 'complete') === 'complete'
              ? <p><strong>Année complète :</strong> les jours fériés chômés sont inclus dans la mensualisation (pas de déduction).</p>
              : <p><strong>Année incomplète :</strong> un jour férié en semaine non travaillée n'a pas d'impact sur la rémunération.</p>
            }
          </div>
        </div>
      </div>

      {data.working_days.length === 0 && (
        <div className="p-3 rounded-lg border border-[var(--warning)]/30 bg-[var(--warning)]/5 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-[var(--warning)]" />
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            <strong>Attention :</strong> Sans planning défini (jours de travail), les jours fériés ne peuvent pas être automatiquement rémunérés.
          </p>
        </div>
      )}

      {loadingHolidays ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Array.from({length: 6}).map((_, i) => (
            <div key={`skel-${i}`} className="h-10 bg-[var(--neutral-100)] animate-pulse rounded-lg" />
          ))}
        </div>
      ) : availableHolidays.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto pr-1">
          {availableHolidays.map((h) => {
            const isMayFirst = h.is_may_first === true;
            const isWorked = data.worked_holidays.includes(h.date);
            const displayDate = new Date(h.date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
            const dayName = new Date(h.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short' });
            return (
              <div
                key={h.date}
                className={cn(
                  'flex items-center justify-between p-2 rounded-lg border transition-colors duration-150',
                  isMayFirst
                    ? (isWorked ? 'border-[var(--error)] bg-[var(--error)]/5 cursor-pointer' : 'border-[#E17A73]/60 bg-[#E17A73]/5 cursor-pointer')
                    : (isWorked ? 'border-[var(--secondary-400)] bg-[var(--secondary-400)]/5 cursor-pointer' : 'border-[var(--neutral-200)] bg-[var(--neutral-50)] cursor-pointer'),
                )}
                onClick={() => onToggleHolidayWorked(h.date)}
                data-testid={`holiday-${h.date}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className={cn(
                    'w-2 h-2 rounded-full shrink-0',
                    isMayFirst ? 'bg-[#E17A73]'
                    : h.scope === 'regional' ? 'bg-[var(--primary-400)]'
                    : h.scope === 'drom' ? 'bg-[var(--primary-600,#4a7c7e)]'
                    : 'bg-[#E17A73]'
                  )} />
                  <div className="min-w-0">
                    <span className="text-xs font-medium block truncate" style={{ color: 'var(--text-primary)' }}>
                      {h.name}
                      {isMayFirst && <span className="text-[10px] text-[var(--error)] ml-1 font-bold">(Obligatoire)</span>}
                    </span>
                    <span className="text-[10px] text-[var(--text-tertiary)]">
                      {dayName} {displayDate}
                      {h.scope === 'regional' ? ' · Régional' : ''}
                      {h.scope === 'drom' ? ' · DROM' : ''}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                  {isMayFirst && isWorked && (
                    <Badge className="text-[9px] bg-[var(--error)] text-white hover:bg-[var(--error)]">200%</Badge>
                  )}
                  <Badge
                    variant={isWorked ? 'default' : 'outline'}
                    className={cn(
                      'text-[10px]',
                      isMayFirst && isWorked ? 'bg-[var(--error)] text-white hover:bg-[var(--error)]'
                      : isWorked ? 'bg-[var(--secondary-400)] text-white hover:bg-[var(--secondary-400)]'
                      : 'text-[var(--text-secondary)]'
                    )}
                  >
                    {isWorked ? 'Travaillé' : 'Chômé'}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-[var(--text-secondary)] italic">Entrez un code postal pour afficher les jours fériés</p>
      )}

      {/* May 1st warning */}
      {data.worked_holidays.some(d => d.endsWith('-05-01')) && (
        <div className="p-3 rounded-lg border border-[var(--error)]/30 bg-[var(--error)]/5 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-[var(--error)]" />
          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            <p><strong className="text-[var(--error)]">1er Mai travaillé :</strong> La rémunération est <strong>obligatoirement doublée (200%)</strong> — ce taux est d'ordre public et ne peut pas être réduit contractuellement.</p>
          </div>
        </div>
      )}

      {/* Surcharge rate for worked holidays */}
      {data.worked_holidays.length > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-[var(--secondary-400)]/30 bg-[var(--secondary-400)]/5">
          <Info className="h-4 w-4 shrink-0" style={{ color: 'var(--secondary-400)' }} />
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Majoration jours fériés travaillés (hors 1er Mai) :</span>
            <div className="flex items-center gap-1">
              <Input type="number" min="0" max="200" step="5" className="w-16 h-7 text-xs text-center" value={data.holiday_surcharge_rate} onChange={(e) => onChange({ ...data, holiday_surcharge_rate: e.target.value })} data-testid="wizard-surcharge-rate" />
              <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>%</span>
            </div>
            <Badge variant="outline" className="text-[10px] border-[var(--secondary-400)] text-[var(--secondary-400)]">
              {data.worked_holidays.filter(d => !d.endsWith('-05-01')).length} jour{data.worked_holidays.filter(d => !d.endsWith('-05-01')).length > 1 ? 's' : ''} travaillé{data.worked_holidays.filter(d => !d.endsWith('-05-01')).length > 1 ? 's' : ''}
              {data.worked_holidays.some(d => d.endsWith('-05-01')) && ' + 1er Mai (200%)'}
            </Badge>
          </div>
        </div>
      )}
      <div>
        <Label>Notes complémentaires</Label>
        <Textarea value={data.schedule_notes} onChange={(e) => onChange({ ...data, schedule_notes: e.target.value })} placeholder="Ex: Pause déjeuner 12h-13h, goûter à 16h..." rows={2} data-testid="wizard-schedule-notes" />
      </div>
    </div>
    {children}
  </div>
);
